import { useContext, useState, useCallback, memo, RefObject } from 'react';

import { injectIntl } from 'react-intl';

import { Popup } from '@atlaskit/popup';
import useRemoteModule from '@atlassian/remote-module-loader/use-remote-module';

import { useRemoteSwitcherPlugin } from '../../utils/use-remote-switcher-plugin';
import {
  BaseSwitcher,
  SwitcherButton,
} from '../atlassian-switcher/atlassian-switcher';

import { HorizontalNavContext } from './horizontal-nav-context';
import * as styled from './horizontal-nav.style';

type Props = {
  tenantId?: string;
  isBeingRenderedInsideMobileNav?: boolean;
  publishUiEvent: (event: Record<string, any>) => void;
};

const RemoteSwitcherPreloader = () => {
  const remoteSwitcherPlugin = useRemoteSwitcherPlugin();
  useRemoteModule(remoteSwitcherPlugin);

  return null;
};

export const HorizontalAppSwitcher = injectIntl<Props>(
  memo(({ isBeingRenderedInsideMobileNav, tenantId }) => {
    const { isLoggedIn } = useContext(HorizontalNavContext);
    const [isOpen, setIsOpen] = useState(false);
    const [hasHovered, setHasHovered] = useState(false);
    const onClose = () => setIsOpen(false);

    const toggleOpen = useCallback(() => {
      setIsOpen(prevState => !prevState);
    }, []);

    if (!isLoggedIn) {
      return null;
    }

    const switcherButton = (
      <SwitcherButton
        isSelected={isOpen}
        onToggleAtlassianSwitcher={toggleOpen}
        onMouseEnter={() => setHasHovered(true)}
      />
    );

    return (
      <styled.HorizontalNavItem>
        <Popup
          isOpen={isOpen}
          onClose={onClose}
          {...(isBeingRenderedInsideMobileNav
            ? {
                placement: 'right-end',
                offset: [
                  180, // manually add this space to avoid popup going over the top edge
                  8, // this is the default value set by popup to give 8px space from the trigger to popup
                ],
                shouldFitViewport: true,
              }
            : { placement: 'bottom-start' })}
          trigger={({ ref, ...triggerProps }) => (
            <div
              {...triggerProps}
              ref={ref as RefObject<HTMLDivElement>} // fix ref type error
            >
              {hasHovered && <RemoteSwitcherPreloader />}
              {switcherButton}
            </div>
          )}
          content={() => (
            <div
              onTouchStart={e => e.stopPropagation()}
              onTouchMove={e => e.preventDefault()}
            >
              <BaseSwitcher appearance="standalone" tenantId={tenantId} />
            </div>
          )}
        />
      </styled.HorizontalNavItem>
    );
  })
);
